import { useState } from "react";
import { Col, Row } from "react-grid-system";
import { Button, mergeClasses, MessageBar, MessageBarBody, Spinner, Text, Tooltip } from "@fluentui/react-components";
import { CopyFilled, EyeFilled, EyeOffFilled, KeyFilled } from "@fluentui/react-icons";
import Status from "../../../../enums/Status";
import passwordService from "../../../../services/passwordService";
import { useTranslation } from "react-i18next";
import IPassword from "../../../../models/password/IPassword";
import useStyleUtils from "../../../../utilities/styles";
import styles from './PasswordDetail.module.scss';

const CopyPassword = (props: { password: string }) => {
    const { t } = useTranslation();

    const handleCopyClicked = () => navigator.clipboard.writeText(props.password);
    
    const style = useStyleUtils();

    return (
        <Tooltip content={t("general.copyToClipboard")} relationship='label'>
            <Button size="small" icon={<CopyFilled />} onClick={handleCopyClicked} />
        </Tooltip>
    );
};

const Error = () => {
    const { t } = useTranslation();

    const style = useStyleUtils();

    return (
        <Col xs={12} className={style.mt2}>
            <MessageBar intent="error">
                <MessageBarBody style={{ whiteSpace: "pre-wrap" }}>
                    {t("passwords.errors.getPassword")}
                </MessageBarBody>
            </MessageBar>
        </Col>
    );
};

export default function Password(props: { password: IPassword }) {
    const { t } = useTranslation();

    const style = useStyleUtils();

    const [status, setStatus] = useState<Status>(Status.NEUTRAL);
    const [showPassword, setShowPassword] = useState(false);
    const [decryptedPassword, setDecryptedPassword] = useState<string>(null!);

    const handleVisibilityClicked = () => {
        if (showPassword) {
            setShowPassword(false);
            return;
        }

        if (decryptedPassword) {
            setShowPassword(true);
            return;
        }

        setStatus(Status.LOADING);
    
        passwordService.getPasswordValue(props.password.id)
            .then(value => {
                setDecryptedPassword(value);
                setShowPassword(true);
                setStatus(Status.LOAD_SUCCESS);
            })
            .catch(() => setStatus(Status.LOAD_ERROR));
    };

    const visibilityTooltipText = showPassword
        ? t("passwords.visibility.toggleHide")
        : t("passwords.visibility.toggleShow");

    const visibilityIcon = showPassword
        ? <EyeOffFilled />
        : <EyeFilled />;

    const passwordClassName = mergeClasses(
        styles.detail,
        showPassword
            ? styles.password
            : null!
    );

    return (
        <Row align="center">
            <Col xs="content" className={style.textCenter}>
                <KeyFilled />
            </Col>
            <Col>
                <Text size={400} className={passwordClassName}>
                    {showPassword ? decryptedPassword : '••••••'}
                </Text>
            </Col>
            <Col xs="content" className={style.textRight}>
                {
                    showPassword && <CopyPassword password={decryptedPassword} />
                }
                <Tooltip content={visibilityTooltipText} relationship='label'>
                    {
                        status === Status.LOADING
                            ? <Spinner size="extra-small" />
                            : <Button size="small" icon={visibilityIcon} onClick={handleVisibilityClicked} />
                    }
                </Tooltip>
            </Col>
            {
                status === Status.LOAD_ERROR && <Error />
            }
        </Row>
    );
};